import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';

import { Autocomplete, Box, Chip, MenuItem, Switch, TextField } from '@mui/material';

import {
  ActivityActor,
  ActivityActorType,
  ActivityCategory,
  ActivityOperation,
  getActivityActors,
} from '../../api/activityApi/activityApi';
import { getCases } from '../../api/casesApi/casesApi';
import {
  CATEGORY_OPTIONS,
  ENTITY_OPTIONS,
  MAX_RANGE_DAYS,
  OPERATION_OPTIONS,
  toDateInput,
} from './activityHelpers';
import { DROPDOWN_PAPER_SX, useActivityStyles } from './activityStyles';

/** The select fields: an empty value shows its "All …" option; the menu is dark like the app's. */
const SELECT_PROPS = { displayEmpty: true, MenuProps: { PaperProps: { sx: DROPDOWN_PAPER_SX } } };

export interface ActivityPanelFilters {
  from: Date;
  to: Date;
  actorId: string;
  caseId: number | null;
  operation: '' | ActivityOperation;
  category: '' | ActivityCategory;
  failuresOnly: boolean;
  entities: string[];
}

interface MatterOption {
  id: number;
  label: string;
}

interface ActivityFiltersPanelProps {
  filters: ActivityPanelFilters;
  onChange: (filters: ActivityPanelFilters) => void;
  actorLabel: string;
  actorTypes: ActivityActorType[];
}

const ActivityFiltersPanel = ({
  filters,
  onChange,
  actorLabel,
  actorTypes,
}: ActivityFiltersPanelProps) => {
  const { classes, cx } = useActivityStyles();

  const [actors, setActors] = useState<ActivityActor[]>([]);
  const [matterSearch, setMatterSearch] = useState('');
  const [matterOptions, setMatterOptions] = useState<MatterOption[]>([]);
  const [selectedMatter, setSelectedMatter] = useState<MatterOption | null>(null);

  const actorTypesKey = actorTypes.join(',');

  // People with activity in the selected range, for the person dropdown.
  useEffect(() => {
    getActivityActors(filters.from, filters.to, actorTypes)
      .then((res) => setActors(res.data.resultSet))
      .catch(() => setActors([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.from, filters.to, actorTypesKey]);

  // Matters: search the firm's cases by reference.
  useEffect(() => {
    const timer = setTimeout(() => {
      getCases({ page: 1, size: 20, match: matterSearch, includeArchived: true })
        .then((res) =>
          setMatterOptions(
            res.data.resultSet.map((c) => ({ id: c.id, label: `#${c.referenceId}` })),
          ),
        )
        .catch(() => setMatterOptions([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [matterSearch]);

  const setDate = (key: 'from' | 'to', value: string) => {
    if (!value) return;
    const date =
      key === 'from' ? moment(value).startOf('day').toDate() : moment(value).endOf('day').toDate();
    const next = { ...filters, [key]: date };
    // Keep the range valid and within the API limit.
    if (next.to <= next.from) {
      if (key === 'from') next.to = moment(next.from).endOf('day').toDate();
      else next.from = moment(next.to).startOf('day').toDate();
    }
    if (moment(next.to).diff(next.from, 'days') > MAX_RANGE_DAYS) {
      if (key === 'from') next.to = moment(next.from).add(MAX_RANGE_DAYS, 'days').toDate();
      else next.from = moment(next.to).subtract(MAX_RANGE_DAYS, 'days').toDate();
    }
    onChange(next);
  };

  const toggleEntity = (label: string) => {
    const entities = filters.entities.includes(label)
      ? filters.entities.filter((entity) => entity !== label)
      : [...filters.entities, label];
    onChange({ ...filters, entities });
  };

  const actorOptions = useMemo(
    () => [{ actorId: '', name: `All ${actorLabel.toLowerCase()}s` }, ...actors],
    [actors, actorLabel],
  );

  return (
    <Box className={classes.panel}>
      <Box className={classes.filterGrid}>
        <Box>
          <label className={classes.fieldLabel}>Date from</label>
          <TextField
            type="date"
            size="small"
            className={classes.input}
            value={toDateInput(filters.from)}
            onChange={(e) => setDate('from', e.target.value)}
          />
        </Box>
        <Box>
          <label className={classes.fieldLabel}>Date to</label>
          <TextField
            type="date"
            size="small"
            className={classes.input}
            value={toDateInput(filters.to)}
            onChange={(e) => setDate('to', e.target.value)}
          />
        </Box>
        <Box>
          <label className={classes.fieldLabel}>Matter</label>
          <Autocomplete
            size="small"
            className={classes.input}
            options={matterOptions}
            value={selectedMatter}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onInputChange={(_e, value) => setMatterSearch(value.replace(/^#/, ''))}
            onChange={(_e, value) => {
              setSelectedMatter(value);
              onChange({ ...filters, caseId: value?.id ?? null });
            }}
            componentsProps={{ paper: { sx: DROPDOWN_PAPER_SX } }}
            renderInput={(params) => <TextField {...params} placeholder="All matters" />}
          />
        </Box>
        <Box>
          <label className={classes.fieldLabel}>{actorLabel}</label>
          <TextField
            select
            size="small"
            SelectProps={SELECT_PROPS}
            className={classes.input}
            value={filters.actorId}
            onChange={(e) => onChange({ ...filters, actorId: e.target.value })}
          >
            {actorOptions.map((actor) => (
              <MenuItem key={actor.actorId || 'all'} value={actor.actorId}>
                {actor.name}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <label className={classes.fieldLabel}>Activity type</label>
          <TextField
            select
            size="small"
            SelectProps={SELECT_PROPS}
            className={classes.input}
            value={filters.operation}
            onChange={(e) =>
              onChange({
                ...filters,
                operation: e.target.value as ActivityPanelFilters['operation'],
              })
            }
          >
            {OPERATION_OPTIONS.map((option) => (
              <MenuItem key={option.value || 'all'} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <label className={classes.fieldLabel}>Category</label>
          <TextField
            select
            size="small"
            SelectProps={SELECT_PROPS}
            className={classes.input}
            value={filters.category}
            onChange={(e) =>
              onChange({ ...filters, category: e.target.value as ActivityPanelFilters['category'] })
            }
          >
            {CATEGORY_OPTIONS.map((option) => (
              <MenuItem key={option.value || 'all'} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box>
          <label className={classes.fieldLabel}>&nbsp;</label>
          <Box className={classes.switchBox}>
            Failures only
            <Switch
              size="small"
              checked={filters.failuresOnly}
              onChange={(e) => onChange({ ...filters, failuresOnly: e.target.checked })}
            />
          </Box>
        </Box>
      </Box>

      <Box mt="16px">
        <label className={classes.fieldLabel}>Entity</label>
        <Box className={classes.chips}>
          {ENTITY_OPTIONS.map((option) => {
            const active = filters.entities.includes(option.label);
            return (
              <Chip
                key={option.label}
                label={option.label}
                variant="outlined"
                clickable
                onClick={() => toggleEntity(option.label)}
                className={cx(classes.chip, active && classes.chipActive)}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default ActivityFiltersPanel;
